// viewDocsClient (WEL-144) — the wire types + fetchers for "View Docs".
//
//   Miles: "We would like a consistent way to view everything that has been
//   sent out regardless of its state. Sent, partially signed, partially
//   completed etc. Shouldn't have to download a PDF etc just view everything."
//
// Endpoints are spec §C3. These shapes are the SETTLED contract with W5 (list,
// detail, file) and W9 (record, resend) — not a guess. Two of their decisions
// shape this file and are worth knowing before you edit it:
//
//   • The record body is typed by DOMAIN FIELD NAME, not by replica label,
//     because every write runs through the recipient's own validators
//     (normalizeDesignation, normalizeAnimal, the landlord's zod schema). A
//     label-keyed map would need a label→field mapping somewhere, which is the
//     drift the declarative replica exists to prevent, just moved elsewhere.
//     So the record bodies below are per-kind and reuse the EXISTING
//     submission types the recipient's own form posts.
//
//   • There is no `section` in the record body — the KIND names the section.
//     intake_designation / intake_yard_care / intake_animals are three separate
//     documents, so the staff form belongs to the document, not to a section
//     inside it.
//
// ViewDocPanel / ViewDocReplica mirror apps/api/src/lib/viewDocs/types.ts
// verbatim — the web app cannot import across the workspace boundary, so the
// shapes are restated here and any drift is a compile error at the first field
// the viewer reads.
//
// The snapshot rule the whole surface rests on: a panel describes THE VERSION
// BEING VIEWED, never live data. The client honours it by re-fetching the
// detail whenever the selected version changes rather than re-using the panel
// it already has.

import { api } from '@/lib/api';
import type { DocSurface } from '@/lib/viewDocs';
import type { AnimalInput } from '@/lib/intakeClient';
import type { VerificationSubmissionBody } from '@/lib/verificationClient';
import type { OwnerIntakeAnswers, OwnerIntakeQuestion } from '@/lib/ownerIntakeClient';

// ---------------------------------------------------------------------------
// Shared shapes (mirror of apps/api/src/lib/viewDocs/types.ts)
// ---------------------------------------------------------------------------

/** One label/value line in a panel. */
export interface ViewDocRow {
  label: string;
  value: string;
  /** Renders muted. "Not yet answered" — never omitted instead. */
  pending?: boolean;
  /** Renders emphasised. Sparingly: money totals, the operative date. */
  emphasis?: boolean;
  /**
   * The value is a typed-name e-signature; render it as a signature block
   * (signature face on a line, "Electronically signed · signedAt" beneath).
   */
  signature?: { signedAt?: string };
}

/** The bespoke variables panel shown above the document. */
export interface ViewDocPanel {
  groups: Array<{
    /** Omitted on the lead group — it renders without a heading. */
    heading?: string;
    rows: ViewDocRow[];
  }>;
  /** One-line summary shown on the collapsed list row. */
  digest: string;
}

export type ReplicaSectionStatus = 'not_requested' | 'requested' | 'completed' | 'cancelled';

export interface ViewDocReplicaField {
  label: string;
  /** 'signature' — a typed-name e-signature; `value` is the typed name. */
  kind: 'text' | 'choice' | 'list' | 'address' | 'signature';
  /** Undefined when the recipient has not answered — ALWAYS emitted anyway. */
  value?: string | string[];
  /** For 'choice': every option they see, so an unanswered choice shows both. */
  options?: string[];
  /** For 'signature': when it was signed, already formatted for display. */
  signedAt?: string;
}

/**
 * A read-only replica of the recipient's own form, in their field order.
 *
 * Two properties W4 guarantees and this client must not undo: unanswered
 * fields are still emitted (absence is the information), and each tenant-intake
 * animal is its OWN section ("Your animals · Rex") rather than a repeated group
 * — because the replica is meant to look like their page, not like our summary
 * of it. So sections are rendered in order, never merged or de-duplicated by
 * heading.
 */
export interface ViewDocReplica {
  sections: Array<{
    heading: string;
    status: ReplicaSectionStatus;
    fields: ViewDocReplicaField[];
  }>;
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export type DocAnchor = 'household' | 'owner';

export interface DocListRow {
  kind: string;
  id: string;
  /** Registry noun — "Lease". */
  label: string;
  /** Label plus context — "Lease · 108 SE 6th #8". */
  title?: string;
  surface: DocSurface;
  anchor: DocAnchor;
  state: string;
  stateLabel?: string;
  /** ViewDocPanel.digest of the current version. */
  digest?: string;
  /** "Jose Cuevas Cortez, Anna Cortez" — searchable. */
  people?: string;
  propertyLabel?: string | null;
  unitLabel?: string | null;
  sentAt?: string | null;
  updatedAt?: string;
  completedAt?: string | null;
  caseId?: string | null;
  caseKind?: DocAnchor | null;
  /** Always null today — the thread registry is not this endpoint's to resolve. */
  threadId?: string | null;
  versionCount?: number;
  viewable?: boolean;
  /**
   * The server's answer to "can this row be sent again" — the KIND has a resend
   * path AND the document is still outstanding. Drawn from, never guessed at: a
   * button that 409s reads as a broken page rather than as a document that is
   * already finished.
   */
  resendable?: boolean;
  /** A W-9: panel and timeline yes, bytes never (the PDF carries an SSN/EIN). */
  redacted?: boolean;
  /**
   * PRESENT BUT NOT YOURS. An accommodation record seen by a viewer off the
   * RRA roster: the row exists so the count is honest, and carries NOTHING
   * about the record — no people, no property, no digest, no state.
   *
   * Different from `redacted`, which withholds only the bytes.
   */
  withheld?: boolean;
  /** Why, in the server's words. */
  withheldReason?: string;
}

export interface DocFacetOption {
  value: string;
  label: string;
  /**
   * REQUIRED, because the server always sends it: `ViewDocFacet.count` on the
   * API side is `count: number`, and every facet builder — the docs list and
   * `sentItems/list.ts`'s `facetsFor` alike — counts before it emits. Declaring
   * it optional here said a chip might arrive with no number, which is a case
   * no server produces and every renderer then has to pretend to handle.
   *
   * `sentItemsClient.SentItemFacet` is this shape, so the contract-parity test
   * (apps/api/src/lib/sentItems/contractParity.test.ts) binds the two.
   */
  count: number;
}

/**
 * A document kind we hold but cannot render, because the registry is
 * default-deny and nobody has written its panel yet.
 *
 * Surfaced rather than swallowed: showing fewer documents than exist, silently,
 * is the opposite of "full transparency for the operator".
 */
export interface DocGap {
  kind: string;
  count: number;
}

/**
 * A whole KIND withheld from this viewer — the shape the household docket
 * already uses (`DocketNote`). The Docs list may report withholding either
 * per-row (`DocListRow.withheld`) or per-kind here; the page renders both, so
 * whichever the server sends, the operator is told.
 */
export interface DocWithheldNote {
  kind: string;
  label?: string;
  reason: string;
}

export interface DocsListResponse {
  items: DocListRow[];
  /** Rows matching the query across ALL pages, not just this one. */
  total?: number;
  nextCursor?: string | null;
  facets?: { kinds?: DocFacetOption[]; states?: DocFacetOption[] };
  gaps?: DocGap[];
  /** Kinds this viewer may not read at all. Not a gap — we know where it is. */
  withheld?: DocWithheldNote[];
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export interface DocVersion {
  id: string;
  label?: string;
  state?: string;
  stateLabel?: string;
  createdAt?: string;
  sentAt?: string | null;
  /** Exactly one is true; honours ?version=. */
  current?: boolean;
  /** False when this version's bytes cannot be served. */
  viewable?: boolean;
}

export interface DocRecipient {
  id: string;
  name: string;
  email?: string | null;
  role?: string;
  roleLabel?: string;
  status?: string;
  statusLabel?: string;
  canResend?: boolean;
  /** The id POST /api/requests/resend understands, when the caller needs it. */
  resendId?: string;
}

/** Bucketed for icons; `kind` alongside carries the precise stored event. */
export type DocActivityType =
  | 'sent'
  | 'reminded'
  | 'opened'
  | 'page_opened'
  | 'responded'
  | 'signed'
  | 'completed'
  | 'declined'
  | 'cancelled'
  | 'voided'
  | 'recorded'
  | 'other';

/**
 * Fill-on-behalf attribution (§C5).
 *
 * W9 deliberately emits NO separate 'recorded' event: a staff-typed completion
 * IS a completion, and two events would imply two things happened. The
 * attribution rides on the existing completed/responded event instead, and the
 * rail renders that line differently when it is present.
 */
export interface DocActivityMeta {
  recordedOnBehalf?: boolean;
  recordedBy?: string;
  recordedByDisplay?: string;
  onBehalfOf?: string;
  onBehalfOfDisplay?: string;
  channel?: string;
}

export interface DocActivityEvent {
  at: string;
  /** Bucketed union — drives the marker. */
  type?: DocActivityType;
  /** The precise stored event name. */
  kind?: string;
  text: string;
  recipient?: string | null;
  recipientName?: string | null;
  /**
   * True when the entry was INFERRED from a first-instant timestamp rather than
   * read from an event row — documents predating the event log still show a
   * history. Rendered with a marker: it is not equal-confidence.
   */
  synthesized?: boolean;
  meta?: DocActivityMeta;
}

export interface DocFile {
  available: boolean;
  /** Ready-made version-scoped path; use it rather than composing one. */
  url?: string;
  filename?: string;
  /** Machine-readable. 'redacted' is real — a W-9 never shows its bytes. */
  reason?: 'never_generated' | 'not_available' | 'redacted' | string;
  /** The sentence to render as-is. */
  note?: string;
}

export interface DocDetail {
  kind: string;
  id: string;
  label: string;
  title?: string;
  /** The headline: who this document is about. */
  subject: string;
  surface: DocSurface;
  anchor: DocAnchor;
  propertyLabel?: string | null;
  unitLabel?: string | null;
  caseId?: string | null;
  caseKind?: DocAnchor | null;
  threadId?: string | null;
  state?: string;
  stateLabel?: string;
  sentAt?: string | null;
  completedAt?: string | null;
  /** The panel FOR THE SELECTED VERSION. */
  panel: ViewDocPanel;
  /** surface === 'form' only. */
  replica?: ViewDocReplica;
  /** surface === 'pdf' only. */
  file?: DocFile;
  /** Every version, including superseded / voided / cancelled ones. */
  versions: DocVersion[];
  selectedVersionId?: string;
  recipients?: DocRecipient[];
  activity?: DocActivityEvent[];
  /** The "still to happen" checklist — one line per outstanding party. */
  pending?: string[];
  /** Registry staffMayFill(). ALWAYS false for a 'pdf' — see mayRecordOnBehalf. */
  fillOnBehalf?: boolean;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function qs(params: Record<string, string | number | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * The addressing pair for one document, as path segments.
 *
 * `id` is the groupId, or `groupId:tail` where a tail exists (a rental
 * verification's sort key, an owner intake's owner index) — the server splits
 * on the FIRST colon, so the whole thing is encoded as one segment.
 */
export function docPath(kind: string, id: string, tail = ''): string {
  return `/api/docs/${encodeURIComponent(kind)}/${encodeURIComponent(id)}${tail}`;
}

export function fetchDocs(
  params: {
    q?: string;
    kind?: string | null;
    state?: string | null;
    cursor?: string | null;
    limit?: number;
  } = {},
): Promise<DocsListResponse> {
  return api.get<DocsListResponse>(
    `/api/docs${qs({
      q: params.q,
      kind: params.kind,
      state: params.state,
      cursor: params.cursor,
      limit: params.limit,
    })}`,
  );
}

/** One document. Omit `versionId` for the current version. */
export function fetchDoc(
  kind: string,
  id: string,
  versionId?: string | null,
): Promise<DocDetail> {
  return api.get<DocDetail>(docPath(kind, id, qs({ version: versionId })));
}

/**
 * The document's bytes, as a Blob the browser renders inline.
 *
 * NOT a download link — that is the exact complaint this feature answers. The
 * caller wraps this in an object URL and hands it to an <iframe>; the object
 * URL is revoked on unmount. Pass `file.url` when the detail supplied one; it
 * is already version-scoped.
 */
export function fetchDocFile(
  kind: string,
  id: string,
  versionId?: string | null,
  url?: string,
): Promise<Blob> {
  return api.blob(
    url ?? docPath(kind, id, `/file${qs({ version: versionId })}`),
    'application/pdf',
  );
}

// ---------------------------------------------------------------------------
// Record on behalf (§C5)
// ---------------------------------------------------------------------------

/** How the answers actually reached WHP. Stamped into the audit trail. */
export type RecordChannel = 'email' | 'phone' | 'in_person' | 'mail' | 'other';

/** The envelope every record body carries, whatever the kind. */
export interface RecordEnvelope {
  /** Recipient id. Derived server-side when unambiguous; 400 if not a party. */
  onBehalfOf?: string;
  /** Their name, as we already rendered it — it lands in the stamp. */
  onBehalfOfDisplay?: string;
  channel?: RecordChannel;
  /** "emailed her answers 8/19, transcribed verbatim". */
  note?: string;
}

/** Reuses the SAME types the recipient's own form posts — see the file header. */
export type RecordAnswers =
  | { designation: import('@/lib/intakeClient').IntakeDesignationData }
  | { yardCare: { arrangement: 'resident_maintains' | 'lessor_arranges' } }
  | { animals: AnimalInput[] }
  | { verification: VerificationSubmissionBody }
  | { answers: OwnerIntakeAnswers };

export type RecordOnBehalfBody = RecordEnvelope & RecordAnswers;

export interface RecordOnBehalfResult {
  ok: boolean;
  kind?: string;
  id?: string;
  recordedAt?: string;
  /** "staff:<sub>". */
  actor?: string;
  recordedBy?: {
    sub: string;
    displayName?: string;
    onBehalfOf?: string;
    onBehalfOfDisplay?: string;
    at?: string;
    channel?: string;
    note?: string;
  };
  /**
   * FALSE when their own answer had already won the completion lock. NOT an
   * error — it means they answered first, which is the outcome we want.
   */
  applied?: boolean;
  /** Safe to surface unedited. */
  message?: string;
}

/**
 * Record a recipient's answers on their behalf.
 *
 * Callers MUST gate on mayRecordOnBehalf() + recordUnavailableReason() first.
 * The API gates independently (403 staff_may_not_sign on any pdf surface, 409
 * use_owning_surface for the accommodation forms) — belt and braces on the one
 * rule that must never fail: staff may fill, staff may never sign.
 */
export function recordDocOnBehalf(
  kind: string,
  id: string,
  body: RecordOnBehalfBody,
): Promise<RecordOnBehalfResult> {
  return api.post<RecordOnBehalfResult>(docPath(kind, id, '/record'), body);
}

// ---------------------------------------------------------------------------
// Resend
// ---------------------------------------------------------------------------

export interface ResendResult {
  ok?: boolean;
  sent: number;
  failed: number;
  results?: Array<{ id: string; ok: boolean; reason?: string; to?: string; at?: string }>;
}

/**
 * Send it again. Omit `recipient` on a signable and every signer still
 * outstanding on the newest submission is nudged.
 *
 * 409 messaging_disabled when WHP_OWN_MESSAGING is off — the same gate the
 * Requests dashboard honours.
 */
export function resendDoc(
  kind: string,
  id: string,
  body: { recipient?: string } = {},
): Promise<ResendResult> {
  return api.post<ResendResult>(docPath(kind, id, '/resend'), body);
}

// ---------------------------------------------------------------------------
// Owner-intake questions (staff-callable)
// ---------------------------------------------------------------------------

/**
 * What the owner would be asked, computed by the SAME
 * `computeApplicableQuestions` their own tokenized form calls — so the
 * conditionals cannot drift from the real form by being copied.
 *
 * This is the reason the owner-intake staff form exists at all: `question.id`
 * IS the OwnerIntakeAnswers key, so the join is a stable field rather than
 * display copy.
 */
export interface OwnerIntakeQuestionsView {
  propertyAddress?: string | null;
  ownerNames?: string[];
  owner?: { role?: string; name?: string } | null;
  /** What is already on file — render the form pre-filled from this. */
  answers: OwnerIntakeAnswers;
  questions: OwnerIntakeQuestion[];
  /** False once the intake is locked; `questions` is then empty. */
  fillable: boolean;
}

/**
 * Re-fetch after every successful record: answering a gate can REVEAL a
 * dependent question, exactly as it does on the owner's own form (answer
 * inHoa=yes and the which-HOA select appears, populated from the live list).
 */
export function fetchOwnerIntakeQuestions(id: string): Promise<OwnerIntakeQuestionsView> {
  return api.get<OwnerIntakeQuestionsView>(docPath('owner_intake', id, '/questions'));
}
